import { Funnel, WarningCircle, X } from "@phosphor-icons/react";
import { MAX_CLAUSE_VALUES, MAX_QUERY_CHARS, MAX_QUERY_CLAUSES, parseQuery } from "@plannello/core/query/parse";
import { m } from "@plannello/i18n/messages";
import type { QueryWarning } from "@plannello/shared";
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { Button as AriaButton, DialogTrigger } from "react-aria-components";
import { PropertyPicker } from "@/components/domain/PropertyPicker";
import { Kbd, Popover, Spinner } from "@/components/ui";
import { useCatalog } from "@/data/CatalogProvider";
import type { Catalog } from "@/data/catalog";
import { registerCommands } from "@/lib/commands";
import { cx } from "@/lib/cx";
import { useMe } from "@/lib/queries";
import { clauseParts, type Suggestion, scopeCatalog, sortKeyLabel, suggest, valuesFor } from "./querySuggest";

/** Splits query text into clause tokens on whitespace outside quotes. */
export function tokenize(q: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (const ch of q) {
    if (ch === '"') quoted = !quoted;
    if (/\s/.test(ch) && !quoted) {
      if (cur) out.push(cur);
      cur = "";
    } else cur += ch;
  }
  if (cur) out.push(cur);
  return out;
}

/** Whether a single token is a complete, recognised clause that should become a chip. */
function isChip(token: string, catalog: Catalog): boolean {
  const ast = parseQuery(token);
  if (ast.sort.length === 1 && ast.clauses.length === 0 && ast.warnings.length === 0) {
    const s = ast.sort[0]!;
    return s.key !== "group" || catalog.labelGroups.some((g) => g.slug === s.group);
  }
  if (ast.clauses.length !== 1) return false;
  const c = ast.clauses[0]!;
  if (c.kind === "field") return ast.warnings.length === 0;
  return !!c.fieldRef && catalog.labelGroups.some((g) => g.slug === c.fieldRef!.key);
}

function split(q: string, catalog: Catalog): { chips: string[]; draft: string } {
  const chips: string[] = [];
  const rest: string[] = [];
  for (const t of tokenize(q)) (isChip(t, catalog) ? chips : rest).push(t);
  return { chips, draft: rest.join(" ") };
}

const compose = (chips: string[], draft: string) => [...chips, draft.trim()].filter(Boolean).join(" ");

export interface FilterBarProps {
  value: string;
  onChange: (q: string) => void;
  warnings: QueryWarning[];
  /** Server full-text part of a hybrid query is in flight. */
  isSearching?: boolean;
  /** Escape from the field moves focus here (the list). */
  onExit?: () => void;
  /** Products of the view: suggestions only offer their labels, statuses and milestones. */
  productIds?: readonly string[];
}

/**
 * Query input with chips. Complete `key:value` clauses turn into chips when followed by a space
 * (or picked from the suggestions); a chip opens a value picker and has a remove button;
 * Backspace at the start pulls the last chip back into the text for editing. Free text stays text.
 * Suggestions follow the token at the caret: keys first, then the values that key accepts.
 * "/" focuses the field from anywhere.
 */
export function FilterBar({ value, onChange, warnings, isSearching, onExit, productIds }: FilterBarProps) {
  const fullCatalog = useCatalog();
  const catalog = useMemo(() => scopeCatalog(fullCatalog, productIds ?? []), [fullCatalog, productIds]);
  const me = useMe().data;
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [chips, setChips] = useState<string[]>(() => split(value, catalog).chips);
  const [draft, setDraft] = useState<string>(() => split(value, catalog).draft);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [caret, setCaret] = useState(0);

  // External changes (clear filters, back button) re-split the text.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to the external value
  useEffect(() => {
    if (value !== compose(chips, draft)) {
      const s = split(value, catalog);
      setChips(s.chips);
      setDraft(s.draft);
    }
  }, [value]);

  useEffect(
    () =>
      registerCommands([
        {
          id: "filter.focus",
          title: () => m.cmd_focus_filter(),
          group: () => m.palette_group_view(),
          icon: Funnel,
          keys: ["/"],
          run: () => inputRef.current?.focus(),
        },
      ]),
    [],
  );

  const emit = (nextChips: string[], nextDraft: string) => {
    setChips(nextChips);
    setDraft(nextDraft);
    onChange(compose(nextChips, nextDraft));
  };

  // The token being typed: from the last unquoted space before the caret to the caret.
  const tokenStart = useMemo(() => {
    let quoted = false;
    let start = 0;
    for (let i = 0; i < caret && i < draft.length; i++) {
      const ch = draft[i]!;
      if (ch === '"') quoted = !quoted;
      if (/\s/.test(ch) && !quoted) start = i + 1;
    }
    return start;
  }, [draft, caret]);
  const token = draft.slice(tokenStart, caret);
  const suggestions = useMemo(() => (open ? suggest(token, catalog, me?.name) : []), [open, token, catalog, me?.name]);
  const showList = open && suggestions.length > 0;

  const accept = (s: Suggestion) => {
    const before = draft.slice(0, tokenStart);
    const after = draft.slice(caret);
    if (s.kind === "key") {
      const next = `${before}${s.insert}${after}`;
      emit(chips, next);
      const pos = before.length + s.insert.length;
      requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(pos, pos);
        setCaret(pos);
      });
      setActive(0);
      return;
    }
    const rest = `${before}${after}`.replace(/\s+/g, " ").trim();
    if (isChip(s.insert, catalog)) emit([...chips, s.insert], rest);
    else emit(chips, `${before}${s.insert} ${after}`.trimStart());
    setActive(0);
    setOpen(false);
  };

  /** Moves complete clauses from the text into chips. */
  const commitDraft = (text: string) => {
    const tokens = tokenize(text);
    const done = tokens.filter((t) => isChip(t, catalog));
    if (done.length === 0) return false;
    const rest = tokens.filter((t) => !isChip(t, catalog)).join(" ");
    emit([...chips, ...done], rest ? `${rest} ` : "");
    return true;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    if (showList && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setActive((a) => (a + (e.key === "ArrowDown" ? 1 : -1) + suggestions.length) % suggestions.length);
      return;
    }
    if (showList && (e.key === "Enter" || e.key === "Tab") && suggestions[active]) {
      e.preventDefault();
      accept(suggestions[active]!);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      commitDraft(draft);
      return;
    }
    if (e.key === " " && el.selectionStart === draft.length && isChip(token, catalog)) {
      e.preventDefault();
      commitDraft(draft);
      setOpen(false);
      return;
    }
    if (e.key === "Backspace" && el.selectionStart === 0 && el.selectionEnd === 0 && chips.length > 0) {
      e.preventDefault();
      const last = chips[chips.length - 1]!;
      const next = `${last}${draft ? ` ${draft}` : ""}`;
      emit(chips.slice(0, -1), next);
      requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(last.length, last.length);
        setCaret(last.length);
      });
      return;
    }
    if (e.key === "Escape") {
      if (showList) {
        e.preventDefault();
        setOpen(false);
        return;
      }
      e.preventDefault();
      inputRef.current?.blur();
      onExit?.();
    }
  };

  const warningFor = (raw: string) => warnings.find((w) => w.raw === raw);
  const firstWarning = warnings[0];

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div
        className={cx(
          "group/filter relative flex min-h-(--input-h) min-w-0 flex-wrap items-center gap-1 rounded-control border border-input-border bg-input-bg px-2 py-1",
          "transition-ui hover:border-input-border-hover focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-focus",
        )}
      >
        <Funnel size={14} aria-hidden="true" className="mr-0.5 shrink-0 text-fg-subtle" />
        {chips.map((raw, i) => (
          <Chip
            // biome-ignore lint/suspicious/noArrayIndexKey: the same clause may appear twice
            key={`${raw}-${i}`}
            raw={raw}
            catalog={catalog}
            meName={me?.name}
            warning={!!warningFor(raw)}
            onChange={(next) => {
              const list = [...chips];
              if (next) list[i] = next;
              else list.splice(i, 1);
              emit(list, draft);
            }}
          />
        ))}
        <input
          ref={inputRef}
          role="combobox"
          aria-label={m.filter_label()}
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList ? `${listId}-${active}` : undefined}
          placeholder={chips.length ? "" : m.filter_placeholder()}
          value={draft}
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => {
            const text = e.target.value;
            const at = e.target.selectionStart ?? text.length;
            // A space after a complete clause turns it into a chip (also for paste and IME input).
            if (at === text.length && /\s$/.test(text) && commitDraft(text)) {
              setOpen(false);
              return;
            }
            setDraft(text);
            setCaret(at);
            setOpen(true);
            setActive(0);
            onChange(compose(chips, text));
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            commitDraft(draft);
          }}
          onKeyDown={onKeyDown}
          className="h-6 min-w-40 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle"
        />
        {isSearching ? (
          <span className="flex items-center gap-1.5 text-xs text-fg-subtle">
            <Spinner className="size-3" />
            <span className="max-sm:hidden">{m.filter_searching()}</span>
          </span>
        ) : null}
        {chips.length || draft ? (
          <AriaButton
            aria-label={m.filter_clear()}
            onPress={() => {
              emit([], "");
              inputRef.current?.focus();
            }}
            className="flex size-6 shrink-0 items-center justify-center rounded-control text-fg-subtle data-hovered:bg-hover data-hovered:text-fg"
          >
            <X size={12} aria-hidden="true" />
          </AriaButton>
        ) : (
          <Kbd keys={["/"]} className="group-focus-within/filter:hidden" />
        )}
        {showList ? (
          <div
            id={listId}
            role="listbox"
            aria-label={m.filter_label()}
            className="popover-anim absolute top-[calc(100%+4px)] left-0 z-40 w-80 max-w-[calc(100vw-32px)] overflow-hidden rounded-panel border border-popover-border bg-popover-bg p-1 shadow-lg"
          >
            {suggestions.map((s, i) => (
              <div
                key={`${s.insert}-${s.label}`}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                tabIndex={-1}
                onMouseDown={(e) => {
                  e.preventDefault();
                  accept(s);
                }}
                onMouseMove={() => setActive(i)}
                onKeyDown={() => undefined}
                className={cx(
                  "flex h-(--control-h-md) cursor-default items-center gap-2 rounded-control px-2 text-sm",
                  i === active ? "bg-active text-fg" : "text-fg",
                )}
              >
                {s.icon ? <span className="flex w-4 shrink-0 justify-center">{s.icon}</span> : null}
                <span className={cx("truncate-title", s.kind === "key" && "font-mono text-xs")}>{s.label}</span>
                {s.description ? (
                  <span className="ml-auto truncate-title pl-3 text-xs text-fg-subtle">{s.description}</span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
      {firstWarning ? (
        <p role="status" className="flex items-center gap-1.5 text-xs text-warning-text">
          <WarningCircle size={14} weight="fill" aria-hidden="true" className="shrink-0" />
          <span className="min-w-0 truncate-title">{warningText(firstWarning)}</span>
        </p>
      ) : null}
    </div>
  );
}

export function warningText(w: QueryWarning): string {
  const field = w.field ?? "";
  const value = w.value ?? w.raw;
  switch (w.code) {
    case "unknown_field":
      return m.warning_unknown_field({ field });
    case "unknown_value":
      return m.warning_unknown_value({ field, value });
    case "invalid_operator":
      return m.warning_invalid_operator({ field });
    case "unsortable_field":
      return m.warning_unsortable({ field: value });
    case "unterminated_quote":
      return m.warning_unterminated();
    case "query_too_long":
      return m.warning_query_too_long({ max: MAX_QUERY_CHARS });
    case "too_many_clauses":
      // A single clause with too many values names its field; otherwise the whole query has too many parts.
      return w.field
        ? m.warning_too_many_values({ field: w.field, max: MAX_CLAUSE_VALUES })
        : m.warning_too_many_clauses({ max: MAX_QUERY_CLAUSES });
    default:
      return m.warning_invalid_value({ field, value });
  }
}

/** One recognised clause: opens a picker over the key's values; the x removes it. */
function Chip({
  raw,
  catalog,
  meName,
  warning,
  onChange,
}: {
  raw: string;
  catalog: Catalog;
  meName?: string;
  warning: boolean;
  onChange: (next: string | null) => void;
}) {
  const ast = parseQuery(raw);
  const clause = ast.clauses[0];
  const sort = ast.sort[0];
  const negate = clause?.negate ?? false;
  const parts = clause ? clauseParts(clause, catalog, meName) : null;
  const key = parts?.key ?? "sort";
  const keyText = parts?.keyLabel ?? m.view_sort_by();
  const values = parts?.values ?? [sort ? (sort.key === "group" ? (sort.group ?? "") : sortKeyLabel(sort.key)) : raw];
  const tokens =
    clause?.kind === "field" ? clause.values : clause?.kind === "text" ? (clause.fieldRef?.values ?? []) : [];
  const options = valuesFor(key, catalog, meName);
  const editable = !!clause && options.length > 0 && (clause.kind === "text" || clause.op === "=");
  const label = `${keyText}${negate ? ` ${m.filter_not()}` : ""}: ${values.join(", ")}`;

  const toggle = (token: string, on: boolean) => {
    const lowerTokens = tokens.map((t) => t.toLowerCase());
    const bare = token.replace(/"/g, "").toLowerCase();
    const next = on ? [...tokens, token] : tokens.filter((_, i) => lowerTokens[i]!.replace(/"/g, "") !== bare);
    if (next.length === 0) onChange(null);
    else
      onChange(
        `${negate ? "-" : ""}${key}:${next.map((t) => (/[\s,]/.test(t) && !t.startsWith('"') ? `"${t}"` : t)).join(",")}`,
      );
  };

  const body = (
    <>
      <span className="text-fg-muted">
        {keyText}
        {negate ? <span className="text-danger-text"> {m.filter_not()}</span> : null}
      </span>
      <span className="truncate-title text-fg">{values.join(", ")}</span>
    </>
  );

  return (
    <span
      className={cx(
        "inline-flex h-6 max-w-72 min-w-0 items-center rounded-control border text-xs",
        warning ? "border-warning-text/50 bg-warning-soft" : "border-line bg-hover",
      )}
    >
      {editable ? (
        <DialogTrigger>
          <AriaButton
            aria-label={m.filter_chip_edit({ clause: label })}
            className="flex h-full min-w-0 items-center gap-1 rounded-l-control pr-1 pl-2 outline-hidden data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus data-hovered:bg-active"
          >
            {body}
          </AriaButton>
          <Popover placement="bottom start" className="overflow-hidden p-0">
            <PropertyPicker
              label={keyText}
              mode="multiple"
              options={options.map((o) => ({ id: o.token, label: o.label, icon: o.icon, description: o.description }))}
              selected={
                new Set(
                  options
                    .filter((o) =>
                      tokens.some(
                        (t) =>
                          t.toLowerCase() === o.token.replace(/"/g, "").toLowerCase() ||
                          t.toLowerCase() === o.token.toLowerCase(),
                      ),
                    )
                    .map((o) => o.token),
                )
              }
              onToggle={toggle}
            />
          </Popover>
        </DialogTrigger>
      ) : (
        <span className="flex min-w-0 items-center gap-1 pr-1 pl-2" title={label}>
          {body}
        </span>
      )}
      <AriaButton
        aria-label={m.filter_chip_remove({ clause: label })}
        onPress={() => onChange(null)}
        className="flex h-full w-5 shrink-0 items-center justify-center rounded-r-control text-fg-subtle outline-hidden data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus data-hovered:bg-active data-hovered:text-fg"
      >
        <X size={10} aria-hidden="true" />
      </AriaButton>
    </span>
  );
}
